import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack,
  Spinner,
  Flex,
  Button,
  Checkbox,
  Radio,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Tooltip,
  ModalFooter
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { RiSearchLine } from "react-icons/ri";
import { Header } from "build-ts/metadata";
import { getSortByLabel } from "../../utils/analysisUtils";
import showToast from "hooks/useCustomToast";
import { deleteCustomAgent, listCustomAgents } from "services/CustomAgentServices";
import CustomModal from "components/modals/CustomModal";
import { MdDirectionsRun } from "react-icons/md";
import ReactMarkdown from "react-markdown";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";


interface LibraryAgentsComponentProps {
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedItems?: Set<string>;
  hideActions?: boolean;
  singleSelection?: boolean;
  onClearSelection?: () => void;
}

const LibraryAgentsComponent: React.FC<LibraryAgentsComponentProps> = ({
  onSelectionChange,
  selectedItems: externalSelectedItems,
  hideActions = false,
  singleSelection = false,
  onClearSelection
}) => {
  const { defaultTimeZone } = useUser();
  const [filteredAgents, setFilteredAgents] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [submittedSearchString, setSubmittedSearchString] = useState("");
  const [sortBy, setSortBy] = useState("CREATED");
  const [sortAscending, setSortAscending] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(externalSelectedItems || new Set());

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const pageSize = 20;

  const fetchAgents = useCallback(async () => {
    setIsLoading(true);
    try {
      const agentsData = await listCustomAgents(
        pageSize,
        currentPage,
        sortBy,
        sortAscending,
        false, // includeScheduleActivity
        "", // activeSchedule
        submittedSearchString // searchString
      );

      setFilteredAgents(agentsData?.headers || []);
      setTotalRows(agentsData?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching agents:", error);
      showToast("Error fetching agents", "Failed to load agents", "error");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, sortAscending, submittedSearchString]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const formatDate = (dateMs: number | undefined) => {
    if (!dateMs) return "N/A";
    try {
      return `${epochConverterWithTimeZone(dateMs || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} ${defaultTimeZone.toUpperCase()}`;
    } catch {
      return "N/A";
    }
  };

  // Handle individual agent selection
  const handleAgentSelection = (agentId: string) => {
    if (singleSelection) {
      // For single selection, replace the current selection
      const newSelectedAgents = new Set([agentId]);
      setSelectedAgents(newSelectedAgents);
      onSelectionChange?.(newSelectedAgents);
    } else {
      // For multiple selection
      const newSelectedAgents = new Set(selectedAgents);
      if (newSelectedAgents.has(agentId)) {
        newSelectedAgents.delete(agentId);
      } else {
        newSelectedAgents.add(agentId);
      }
      setSelectedAgents(newSelectedAgents);
      onSelectionChange?.(newSelectedAgents);
    }
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (singleSelection) {
      // For single selection, select all doesn't make sense, so we'll disable it
      return;
    }
    const newSelectedAgents = selectedAgents.size === filteredAgents.length
      ? new Set<string>()
      : new Set(filteredAgents.map(agent => agent.id!));
    setSelectedAgents(newSelectedAgents);
    onSelectionChange?.(newSelectedAgents);
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedAgents(new Set());
    onSelectionChange?.(new Set());
    onClearSelection?.();
  };

  const handleDeleteAgent = async (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteCustomAgent(deleteId);
      showToast("Agent deleted successfully", "", "success");
      fetchAgents();
      setOpenDeleteModal(false);
      setDeleteId(null);
    }
  };

  // Handle search key down
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSubmittedSearchString(searchString);
      setCurrentPage(1); // Reset to first page when searching
    }
  };

  // Check if all agents are selected
  const isAllSelected = filteredAgents.length > 0 && selectedAgents.size === filteredAgents.length;
  const isIndeterminate = selectedAgents.size > 0 && selectedAgents.size < filteredAgents.length;

  return (
    <Box p={6} height="100%" overflowY="auto">
        {/* Search and Filter Section */}
        <HStack spacing={4} mb={6} justify="space-between">
          <HStack spacing={4}>
            <InputGroup size="md" maxW="400px">
              <InputLeftElement>
                <Icon as={RiSearchLine} color="gray.500" />
              </InputLeftElement>
              <Input
                placeholder="Search agents..."
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                focusBorderColor="purple.600"
                onKeyDown={handleSearchKeyDown}
              />
            </InputGroup>

          {/* Sort By Toggle */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Sort by:
            </Text>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="xs"
                size="xs"
                rounded="md"
                color="fg.default"
                bg="bg.default"
                border="1px"
                borderColor="gray.300"
                _hover={{ bg: "purple.50" }}
                p={3}
                aria-label="Sort by"
                textAlign="left"
                width="150px"
                isTruncated
              >
                {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}
              </MenuButton>
              <MenuList>
                <MenuItem key="last_modified_asc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Last Modified (Asc)
                </MenuItem>
                <MenuItem key="last_modified_desc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Last Modified (Desc)
                </MenuItem>
                <MenuItem key="created_asc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Created At (Asc)
                </MenuItem>
                <MenuItem key="created_desc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Created At (Desc)
                </MenuItem>
                <MenuItem key="name_asc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Name (Asc)
                </MenuItem>
                <MenuItem key="name_desc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Name (Desc)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          {/* Clear selection button for single selection mode */}
          {singleSelection && selectedAgents.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          )}
          {selectedAgents.size > 0 && !hideActions && (
            <Button
              colorScheme="purple"
              size="md"
              onClick={() => {
                // Open all selected agents in new tabs
                selectedAgents.forEach(agentId => {
                  window.open(`/agent/${agentId}`, "_blank");
                });
              }}
              isDisabled={selectedAgents.size === 0}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed"
              }}
            >
              Open {selectedAgents.size > 0 ? `(${selectedAgents.size})` : ""}
            </Button>
          )}
        </HStack>

        <HStack spacing={4}>
          {selectedAgents.size > 0 && !hideActions && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              {selectedAgents.size} selected
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted" mb={0}>
            {filteredAgents.length} results
          </Text>
        </HStack>
      </HStack>

      {/* Table */}
      <TableContainer
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm" width="50px">
                  {singleSelection ? (
                    <Text fontSize="xs" color="gray.500">Select</Text>
                  ) : (
                    <Checkbox
                      isChecked={isAllSelected}
                      isIndeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      colorScheme="purple"
                    />
                  )}
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Agent Name
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Description
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Author
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Created Date
                </Th>
                {!hideActions && (
                  <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                    {/* for delete */}
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {filteredAgents.length === 0 ? (
                <Tr>
                  <Td colSpan={hideActions ? 4 : 5} textAlign="center" py={8} color="fg.muted">
                    {searchString.trim() !== "" ? "No agents match your search" : "No agents found"}
                  </Td>
                </Tr>
              ) : (
                filteredAgents.map((agent) => (
                  <Tr
                    key={agent.id}
                    _hover={{ bg: "gray.50" }}
                    bg={selectedAgents.has(agent.id!) ? "purple.50" : "white"}
                  >
                    <Td>
                      {singleSelection ? (
                        <Radio
                          isChecked={selectedAgents.has(agent.id!)}
                          onChange={() => handleAgentSelection(agent.id!)}
                          colorScheme="purple"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Checkbox
                          isChecked={selectedAgents.has(agent.id!)}
                          onChange={() => handleAgentSelection(agent.id!)}
                          colorScheme="purple"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </Td>
                    <Td
                      fontWeight="medium"
                      color="fg.default"
                      cursor="pointer"
                      maxW="300px"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      <Text mb={0}>{agent.name || "Unnamed Agent"}</Text>
                    </Td>
                    <Td
                      color="fg.muted"
                      width="300px"
                      maxW="300px"
                    >
                      <Tooltip
                        label={
                          <Box maxW="400px">
                            <ReactMarkdown>
                              {agent.description || ""}
                            </ReactMarkdown>
                          </Box>
                        }
                        hasArrow
                        placement="top"
                        isDisabled={!agent.description}
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                      >
                        <Box
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          cursor={agent.description ? "pointer" : "default"}
                          sx={{
                            "& p": {
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }
                          }}
                        >
                          {agent.description === null || agent.description === undefined || agent.description === "" ? "" : agent.description}
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {agent.author_name || "Unknown"}
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {formatDate(agent.created_date_ms)}
                    </Td>
                    {!hideActions && (
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Open Agent Runs">
                            <IconButton
                              aria-label="Agent Runs"
                              icon={<MdDirectionsRun />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "green.500" }}
                              onClick={() => window.open(`/agent/${agent.id}/runs`, "_blank")}
                            />
                          </Tooltip>
                          <Tooltip label="Open Agent">
                            <IconButton
                              aria-label="Open Agent"
                              icon={<ExternalLinkIcon />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "purple.500" }}
                              onClick={() => window.open(`/agent/${agent.id}`, "_blank")}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Agent">
                            <IconButton
                              aria-label="Delete Agent"
                              pl={4}
                              icon={<DeleteIcon />}
                              size="md"
                              colorScheme="red"
                              bg="none"
                              color="grey"
                              _hover={{ color: "red.500" }}
                              onClick={() => handleDeleteAgent(agent.id!)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoading && totalRows > 0 && searchString.trim() === "" && (
        <HStack justify="center" mt={6} spacing={2}>
          {Array.from({ length: Math.ceil(totalRows / pageSize) }, (_, index) => index + 1).map((pageNumber) => (
            <Button
              key={pageNumber}
              size="sm"
              variant={currentPage === pageNumber ? "solid" : "outline"}
              colorScheme={currentPage === pageNumber ? "purple" : "gray"}
              onClick={() => setCurrentPage(pageNumber)}
              minW="40px"
            >
              {pageNumber}
            </Button>
          ))}
        </HStack>
      )}

      {/* Delete Modal */}
      <CustomModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Are you sure want to delete the agent?"
        size="sm"
        maxWidth="600px"
        maxHeight="200px"
        showCloseButton={false}
      >
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={() => handleDelete()}>
            Delete
          </Button>
          <Button colorScheme="purple" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </CustomModal>
    </Box>
  );
};

export default LibraryAgentsComponent;